/**
 * Il rendiconto del mese, come lo legge un property manager.
 *
 * Tre blocchi nell'ordine in cui i soldi si muovono — ricavi, trattenute dei
 * canali, costi della casa — ognuno chiuso da un subtotale, e in fondo la
 * riga che conta: **quanto ti resta**. Accanto a ogni importo c'è la quota
 * sui ricavi, perché «571 €» non dice niente e «23,5% dei ricavi» sì.
 *
 * Ogni riga dichiara da dove viene. Una stima etichettata come tale è utile;
 * una stima spacciata per un dato certo brucia l'unica cosa che stiamo
 * vendendo, che è sapere il numero vero.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Brush, Check, FileText } from "lucide-react";
import {
  formatEur,
  formatMese,
  type ConfrontoBaseline,
  type ProfittoImmobile,
  type Mese,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { centesimiDa, testoDa } from "../lib/importi.ts";
import { messaggioDi } from "../lib/errori.ts";
import { useStore } from "../lib/store.tsx";
import { Card, Pill, cx } from "../ui/primitives.tsx";
import { IntestazioneRiquadro } from "../ui/Sezione.tsx";

export function Rendiconto({
  profitto: p,
  confronto,
  listingId,
  onConfermato,
  onCorreggi,
}: {
  profitto: ProfittoImmobile;
  confronto: ConfrontoBaseline | null;
  listingId: string;
  onConfermato: () => Promise<void>;
  /** Su un mese dichiarato dall'host: riapre il modulo per correggerlo. */
  onCorreggi?: () => void;
}) {
  const r = p.rendiconto;
  const conCanali = r.canali.length > 0;
  // Un mese che il gestionale non conosceva e che l'host ha riempito lui.
  const dichiarato = p.dichiaratoIl ?? null;
  const perCategoria = (categoria: string) =>
    p.righe
      .filter((riga) => riga.categoria === categoria)
      .reduce((s, riga) => s + riga.importoCents, 0);
  // Le commissioni in tabella sommano Booking, incasso e IVA: l'IVA è quel che resta.
  // Su un mese dichiarato le commissioni le ha scritte l'host, tutte insieme:
  // non c'è un quadro per canale da cui scorporare l'IVA, e chiamarle «IVA»
  // sarebbe inventare.
  const iva = dichiarato
    ? 0
    : Math.max(
        0,
        perCategoria("commissioni_ota") -
          r.commissioniDaPagareCents -
          r.incassoCents,
      );
  const altriCosti = p.righe.filter(
    (riga) =>
      riga.categoria !== "commissioni_ota" &&
      riga.categoria !== "tassa_soggiorno",
  );
  const daPagare = r.canali.filter((c) => !c.trattenutaNelPayout);
  const trattenuti = r.canali.filter(
    (c) => c.trattenutaNelPayout && c.commissioneCents > 0,
  );

  const inPerdita = p.utileGestoreCents < 0;
  /**
   * In che stato è il mese lo dice il ledger, non un confronto a mano.
   *
   * `null` vuol dire che chi ha calcolato il conto non ha passato la data di
   * oggi: senza orologio non si indovina, e allora ci si comporta come prima.
   */
  const stato = p.statoMese;
  // Un mese con i numeri inseriti dall'host è finito per definizione: l'ha
  // chiuso lui, anche se il calendario dice che è in corso.
  const finito =
    dichiarato !== null ||
    stato === null ||
    stato === "chiuso" ||
    stato === "in_attesa_incassi";
  /**
   * «Il conto è parziale» si dice solo di un mese finito.
   *
   * Su un mese che deve ancora vendersi non manca un dato: manca il futuro. È
   * la differenza fra **non sapere** e **non essere ancora finito**, e detta
   * male diventa il «te lo dico a fine mese» che l'host si sentiva rispondere
   * chiedendo di ottobre a settembre.
   */
  const parziale =
    !dichiarato && finito && p.nottiConosciute < p.nottiDisponibili;
  // Un conto giusto su dati vecchi, da fuori, è identico a un conto sbagliato.
  // Sotto le sei ore si tace: è la cadenza normale e dirlo sarebbe rumore.
  const invecchiato = daQuanto(p.lettoDalGestionaleIl);

  return (
    <Card className="flex h-full flex-col p-5">
      <IntestazioneRiquadro
        icon={<FileText size={15} />}
        titolo={`Rendiconto di ${formatMese(p.mese)}`}
        nota={
          dichiarato
            ? p.nottiConosciute > 0
              ? `${p.nottiVendute} notti su ${p.nottiDisponibili} · inseriti da te`
              : "inseriti da te"
            : conCanali
              ? `${r.canali.reduce((s, c) => s + c.prenotazioni, 0)} prenotazioni con check-in nel mese`
              : `${p.nottiVendute} notti su ${p.nottiDisponibili}`
        }
      />

      <div className="flex flex-col">
        {conCanali ? (
          <>
            {/* ── Quello che hanno pagato gli ospiti, e cosa è arrivato ── */}
            <Blocco titolo="Venduto">
              <Riga
                etichetta="Venduto lordo"
                nota="senza imposta di soggiorno"
                importo={r.vendutoLordoCents}
              />
              {trattenuti.map((c) => (
                <Riga
                  key={c.canale}
                  etichetta={
                    <span>
                      Commissione <span className="capitalize">{c.canale}</span>
                    </span>
                  }
                  nota="già trattenuta dal canale"
                  importo={-c.commissioneCents}
                  stima={c.stimato}
                />
              ))}
            </Blocco>
            <Subtotale
              etichetta="Incassato"
              nota="il numero di Guesty"
              importo={r.incassatoCents}
            />

            {/* ── Quello che il canale fattura a parte, e il Comune ─────── */}
            <Blocco titolo="Da pagare a parte">
              {daPagare.map((c) => (
                <div key={c.canale} className="contents">
                  {c.commissioneCents > 0 ? (
                    <Riga
                      etichetta={
                        <span>
                          Commissione{" "}
                          <span className="capitalize">{c.canale}</span>
                        </span>
                      }
                      // Di un canale mai visto non sappiamo se versa al lordo
                      // o al netto: il conto lo tratta come se versasse al
                      // lordo, e va detto invece di darlo per certo.
                      nota={
                        c.convenzioneVerificata
                          ? null
                          : "non sappiamo se il canale l'ha già trattenuta: convenzione non verificata"
                      }
                      importo={-c.commissioneCents}
                      stima={c.stimato}
                    />
                  ) : null}
                  {c.incassoCents > 0 ? (
                    <Riga
                      etichetta={
                        <span>
                          Costo di incasso{" "}
                          <span className="capitalize">{c.canale}</span>
                        </span>
                      }
                      nota={
                        c.incassoStimato
                          ? "stimato: il gestionale dichiarava zero"
                          : null
                      }
                      importo={-c.incassoCents}
                      // La stima del costo di incasso è una cosa sua: la
                      // commissione può essere dichiarata e l'incasso no.
                      stima={c.incassoStimato}
                    />
                  ) : null}
                </div>
              ))}
              {r.impostaSoggiornoDaVersareCents > 0 ||
              r.impostaSoggiornoCanaleCents > 0 ? (
                <Riga
                  etichetta="Imposta di soggiorno da versare"
                  nota={
                    r.impostaSoggiornoCanaleCents > 0
                      ? `in tutto ${formatEur(
                          r.impostaSoggiornoDaVersareCents +
                            r.impostaSoggiornoCanaleCents,
                        )}: ${formatEur(r.impostaSoggiornoCanaleCents)} li versa Airbnb`
                      : "arrivata dentro il payout"
                  }
                  importo={-r.impostaSoggiornoDaVersareCents}
                />
              ) : null}
              {daPagare.length === 0 &&
              r.impostaSoggiornoDaVersareCents === 0 ? (
                <Vuota testo="Niente da pagare a parte." />
              ) : null}
            </Blocco>
            <Subtotale
              etichetta="Prima di pulizie e imposte"
              importo={r.primaDiPulizieEImposteCents}
            />
          </>
        ) : dichiarato ? (
          <>
            {/* ── Un mese inserito dall'host: le sue voci, come le ha date ── */}
            <Blocco titolo="Ricavi">
              <Riga
                etichetta="Incassato"
                nota="inserito da te"
                importo={p.ricaviLordiCents}
              />
            </Blocco>
            <Blocco titolo="Da pagare a parte">
              {perCategoria("commissioni_ota") > 0 ? (
                <Riga
                  etichetta="Commissioni pagate a parte"
                  importo={-perCategoria("commissioni_ota")}
                />
              ) : null}
              {perCategoria("tassa_soggiorno") > 0 ? (
                <Riga
                  etichetta="Imposta di soggiorno versata"
                  importo={-perCategoria("tassa_soggiorno")}
                />
              ) : null}
              {perCategoria("commissioni_ota") === 0 &&
              perCategoria("tassa_soggiorno") === 0 ? (
                <Vuota testo="Niente da pagare a parte." />
              ) : null}
            </Blocco>
            <Subtotale
              etichetta="Prima degli altri costi"
              importo={
                p.ricaviLordiCents -
                perCategoria("commissioni_ota") -
                perCategoria("tassa_soggiorno")
              }
            />
          </>
        ) : (
          <>
            {/*
              C1: l'etichetta «nessuna prenotazione in cache» era falsa.
              La prenotazione c'è: ha il check-in in un altro mese, e lì sono
              contati i suoi soldi — come fa il gestionale. Un mese pieno di
              notti e vuoto di euro non è un mese andato male, ed è la prima
              cosa da dire prima che il numero venga letto e creduto.
            */}
            <Blocco titolo="Ricavi">
              {p.nessunCheckInNelMese && p.nottiVendute > 0 ? (
                <Riga
                  etichetta="Nessun soggiorno comincia in questo mese"
                  nota={`${p.nottiVendute} notti occupate da una prenotazione cominciata prima: i suoi soldi contano nel mese del check-in`}
                  importo={0}
                />
              ) : (
                <Vuota testo="Nessuna prenotazione con check-in in questo mese." />
              )}
            </Blocco>
            <Subtotale etichetta="Totale ricavi" importo={p.ricaviLordiCents} />
          </>
        )}

        {/* ── Costi della casa ──────────────────────────────────────── */}
        <Blocco titolo="Costi della casa">
          {/* Le pulizie del mese, in ore: su ogni mese passato o in corso,
              tranne quelli con i conti inseriti a mano (lì sono negli altri
              costi). Prima c'era «quanto costa una pulizia» da cui Otto
              stimava: il 14/10/2026 si è deciso che il numero vero si dà
              mese per mese, nel modo in cui l'host lo sa. */}
          {!dichiarato && stato !== "futuro" ? (
            <PulizieDelMese
              listingId={listingId}
              mese={p.mese}
              stimaCents={p.righe
                .filter(
                  (x) => x.categoria === "pulizie" && x.sorgente === "stimato",
                )
                .reduce((t, x) => t + x.importoCents, 0)}
              onSalvato={onConfermato}
            />
          ) : null}
          {iva > 0 ? (
            <Riga
              etichetta="IVA sulle commissioni"
              nota="che non recuperi"
              importo={-iva}
            />
          ) : null}
          {altriCosti.length === 0 && iva === 0 ? (
            <Vuota testo="Nessun costo registrato." />
          ) : (
            altriCosti.map((riga) => (
              <div key={`${riga.categoria}-${riga.aCaricoDi}`}>
                <Riga
                  etichetta={
                    // «Altro» è il nome della categoria; su un mese
                    // dichiarato è la voce che l'host ha chiamato così.
                    dichiarato && riga.categoria === "altro"
                      ? "Altri costi"
                      : riga.etichetta
                  }
                  importo={-riga.importoCents}
                  stima={riga.sorgente === "stimato"}
                  nota={
                    riga.aCaricoDi === "proprietario"
                      ? "paga il proprietario"
                      : null
                  }
                />
              </div>
            ))
          )}
        </Blocco>
        <Subtotale etichetta="Utile della casa" importo={p.utileCasaCents} />

        {/* ── Chi prende cosa, se c'è qualcuno con cui dividere ─────── */}
        {p.trasferimentoCents > 0 ? (
          <Blocco
            titolo={
              p.tipoRapporto === "affitto" ? "Al proprietario" : "La tua parte"
            }
          >
            <Riga
              etichetta={
                p.tipoRapporto === "affitto"
                  ? "Canone di locazione"
                  : "Fee di gestione"
              }
              importo={
                p.tipoRapporto === "affitto"
                  ? -p.trasferimentoCents
                  : p.trasferimentoCents
              }
            />
          </Blocco>
        ) : null}

        {/* ── La riga che conta ─────────────────────────────────────── */}
        <div
          className={cx(
            "mt-4 flex items-center justify-between gap-3 rounded-card px-4 py-3.5",
            inPerdita ? "bg-danger-soft" : "bg-ink text-ink-inverse",
          )}
        >
          <div>
            <p
              className={cx(
                "text-[15px] font-extrabold",
                inPerdita ? "text-danger" : "text-ink-inverse",
              )}
            >
              Ti resta
            </p>
            <p
              className={cx(
                "text-[12px]",
                inPerdita ? "text-danger/80" : "text-ink-inverse/60",
              )}
            >
              dopo canali, Comune e costi
            </p>
          </div>
          <p
            className={cx(
              "text-[26px] font-extrabold leading-none tabular-nums",
              inPerdita ? "text-danger" : "text-ink-inverse",
            )}
          >
            {formatEur(p.utileGestoreCents)}
          </p>
        </div>
      </div>

      {/* ── Le riserve, in chiaro ─────────────────────────────────────── */}
      <footer className="mt-4 flex flex-col gap-1.5 text-[12.5px] leading-relaxed">
        {dichiarato ? (
          <p className="text-ink-soft">
            Numeri inseriti da te il{" "}
            {new Date(dichiarato).toLocaleDateString("it-IT")}.{" "}
            {onCorreggi ? (
              <button
                type="button"
                onClick={onCorreggi}
                className="font-semibold text-accent underline-offset-2 hover:underline"
              >
                Correggi
              </button>
            ) : null}
          </p>
        ) : null}
        {p.stimatoCents > 0 ? (
          <p className="text-ink-faint">
            ≈ {formatEur(p.stimatoCents)} di costi sono stimati.
          </p>
        ) : null}
        {parziale ? (
          <p className="text-warn">
            Di questo mese ho il calendario di {p.nottiConosciute} giorni su{" "}
            {p.nottiDisponibili}: il conto è parziale.
          </p>
        ) : null}
        {/*
          Un mese che deve ancora finire ha una domanda sua: non «quanto ho
          guadagnato» ma «quanto c'è già in cassa e quante notti restano».
          Finora questa risposta viveva solo in chat.
        */}
        {!finito ? (
          <p className="text-ink-soft">
            {stato === "futuro" ? "Mese futuro: " : "Mese in corso: "}
            <b>{formatEur(p.ricaviLordiCents)}</b> già prenotati
            {p.nottiLibere > 0 ? (
              <>
                , <b>{p.nottiLibere}</b>{" "}
                {p.nottiLibere === 1 ? "notte libera" : "notti libere"}
                {p.valoreNottiLibereCents > 0
                  ? ` (a listino ${formatEur(p.valoreNottiLibereCents)})`
                  : ""}
                .
              </>
            ) : (
              <>, nessuna notte libera.</>
            )}
          </p>
        ) : null}
        {/*
          Ricavi e notti non si contano allo stesso modo, e su un soggiorno a
          cavallo divergono: dirlo costa una riga, non dirlo costa la fiducia
          su tutti e due i numeri.
        */}
        {conCanali && p.convenzioni.ricavi === "check_in" ? (
          <p className="text-ink-faint">
            Incassi contati per data di check-in, come su Guesty.
          </p>
        ) : null}
        {!p.ricaviDaCheckIn ? (
          <p className="text-warn">
            Questi ricavi non vengono dalle prenotazioni con check-in nel mese:
            non sono confrontabili con il gestionale.
          </p>
        ) : null}
        {invecchiato ? (
          <p className="text-warn">
            Questi numeri li ho letti dal gestionale {invecchiato}. Le
            prenotazioni arrivate dopo non ci sono: rileggi da Impostazioni.
          </p>
        ) : null}
        {p.saldoVersoProprietarioCents !== 0 ? (
          <p className="text-ink-soft">
            {p.saldoVersoProprietarioCents > 0 ? (
              <>
                Hai anticipato <b>{formatEur(p.saldoVersoProprietarioCents)}</b>{" "}
                che tocca al proprietario.
              </>
            ) : (
              <>
                Il proprietario ha anticipato{" "}
                <b>{formatEur(-p.saldoVersoProprietarioCents)}</b> che tocca a
                te.
              </>
            )}
          </p>
        ) : null}
        {confronto ? <Confronto confronto={confronto} /> : null}
      </footer>
    </Card>
  );
}

// ── Le parti del rendiconto ─────────────────────────────────────────────────

function Blocco({ titolo, children }: { titolo: string; children: ReactNode }) {
  return (
    <div className="mt-4 first:mt-0">
      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
        {titolo}
      </p>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function Riga({
  etichetta,
  importo,
  stima,
  nota,
}: {
  etichetta: ReactNode;
  importo: number;
  /** Una stima si riconosce dal «≈» davanti al numero, spiegato una volta in fondo. */
  stima?: boolean;
  nota?: string | null;
}) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 text-[14px]">
      {/* La nota va sotto l'etichetta, non accanto: una nota lunga non deve
          mai tagliare la voce a cui si riferisce. */}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-ink">{etichetta}</span>
        {nota ? (
          <span className="truncate text-[11.5px] leading-snug text-ink-faint">
            {nota}
          </span>
        ) : null}
      </span>
      <span
        className={cx(
          "shrink-0 whitespace-nowrap text-right tabular-nums sm:w-32",
          importo < 0 ? "text-ink-soft" : "font-semibold text-ink",
        )}
      >
        {stima ? <span className="mr-0.5 text-ink-faint">≈</span> : null}
        {importo < 0 ? `−${formatEur(-importo)}` : formatEur(importo)}
      </span>
    </div>
  );
}

function Subtotale({
  etichetta,
  nota,
  importo,
}: {
  etichetta: string;
  nota?: string;
  importo: number;
}) {
  return (
    <div className="mt-1 flex items-baseline gap-3 border-t border-line pt-2 text-[14.5px] font-bold">
      <span className="flex-1 text-ink">
        {etichetta}
        {nota ? (
          <span className="ml-1.5 text-[12px] font-normal text-ink-faint">
            {nota}
          </span>
        ) : null}
      </span>
      <span
        className={cx(
          "shrink-0 whitespace-nowrap text-right tabular-nums sm:w-32",
          importo < 0 ? "text-danger" : "text-ink",
        )}
      >
        {importo < 0 ? `−${formatEur(-importo)}` : formatEur(importo)}
      </span>
    </div>
  );
}

function Vuota({ testo }: { testo: string }) {
  return <p className="py-1.5 text-[13px] text-ink-faint">{testo}</p>;
}

/**
 * Le pulizie di un mese: quanto costa l'ora e quante ore sono state fatte.
 *
 * Il conto lo fa Otto e quel mese le pulizie non sono più una stima: il
 * totale sostituisce ogni altra voce di pulizia dello stesso mese (lo fa il
 * server, `ledger-pulizie.ts`). Non passa dalle proposte: non si scrive
 * niente su Guesty, si dà un numero nostro.
 */
function PulizieDelMese({
  listingId,
  mese,
  stimaCents,
  onSalvato,
}: {
  listingId: string;
  mese: Mese;
  /** Quanto Otto sta stimando adesso, per dirlo prima che l'host scriva. */
  stimaCents: number;
  onSalvato: () => Promise<void>;
}) {
  const store = useStore();
  const [dati, setDati] = useState<{
    orarioCents: number;
    ore: number;
    importoCents: number;
  } | null>(null);
  const [aperto, setAperto] = useState(false);
  const [orario, setOrario] = useState("");
  const [ore, setOre] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;
    setDati(null);
    setAperto(false);
    api
      .pulizieDelMese(listingId, mese)
      .then(({ pulizie }) => {
        if (vivo) setDati(pulizie);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [listingId, mese]);

  const apri = () => {
    setOrario(dati ? testoDa(dati.orarioCents) : "");
    setOre(dati ? String(dati.ore).replace(".", ",") : "");
    setAperto(true);
  };

  const orarioCents = centesimiDa(orario);
  const oreNumero = /^\d+([.,]\d{1,2})?$/.test(ore.trim())
    ? Number(ore.trim().replace(",", "."))
    : null;
  const oreValide = oreNumero !== null && oreNumero <= 744;
  const totaleCents =
    orarioCents !== null && oreValide && (oreNumero === 0 || orarioCents > 0)
      ? Math.round(orarioCents * oreNumero)
      : null;
  const nomeMese = formatMese(mese);

  const salva = async () => {
    if (totaleCents === null || oreNumero === null || orarioCents === null)
      return;
    setSalvando(true);
    try {
      await api.salvaPulizieDelMese(listingId, {
        mese,
        orarioCents,
        ore: oreNumero,
      });
      setDati({ orarioCents, ore: oreNumero, importoCents: totaleCents });
      setAperto(false);
      store.notify(`Pulizie di ${nomeMese}: ${formatEur(totaleCents)}.`);
      await onSalvato();
    } catch (e) {
      store.notify(messaggioDi(e, "Non sono riuscito a salvarle."));
    } finally {
      setSalvando(false);
    }
  };

  if (!aperto) {
    return dati ? (
      <div className="mb-2 flex items-center gap-3 rounded-field bg-surface-muted px-3 py-2.5">
        <Brush size={16} className="shrink-0 text-ink-soft" />
        <p className="min-w-0 flex-1 text-[13px] leading-snug text-ink">
          <b>Pulizie di {nomeMese}</b>: {String(dati.ore).replace(".", ",")} ore
          × {formatEur(dati.orarioCents)}/ora ={" "}
          <b className="tabular-nums">{formatEur(dati.importoCents)}</b>
        </p>
        <Pill variant="ghost" size="sm" onClick={apri} className="shrink-0">
          Modifica
        </Pill>
      </div>
    ) : (
      <div className="mb-2 flex items-center gap-3 rounded-field bg-warn-soft px-3 py-2.5">
        <Brush size={16} className="shrink-0 text-warn" />
        <p className="min-w-0 flex-1 text-[13px] leading-snug text-ink">
          <b>Pulizie di {nomeMese}</b>: quante ore e a quanto l&apos;ora?{" "}
          <span className="text-ink-soft">
            {stimaCents > 0
              ? `Ora sono stimate a ${formatEur(stimaCents)}.`
              : "Ora non ne conto nessuna."}
          </span>
        </p>
        <Pill variant="primary" size="sm" onClick={apri} className="shrink-0">
          Inserisci
        </Pill>
      </div>
    );
  }

  const campo =
    "w-full min-w-0 bg-transparent py-2 text-[14px] tabular-nums text-ink outline-none placeholder:text-ink-faint";

  return (
    <div className="mb-2 flex flex-col gap-3 rounded-field bg-surface-muted p-3.5">
      <p className="text-[13px] font-bold text-ink">Pulizie di {nomeMese}</p>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-soft">
            Costo orario
          </span>
          <span
            className={cx(
              "flex items-center gap-1.5 rounded-field bg-surface px-2.5",
              orarioCents === null && "ring-1 ring-danger",
            )}
          >
            <span className="text-sm text-ink-faint">€</span>
            <input
              type="text"
              inputMode="decimal"
              value={orario}
              placeholder="15"
              aria-label="Costo orario"
              onChange={(e) => setOrario(e.target.value)}
              className={campo}
            />
            <span className="text-[12px] text-ink-faint">/ora</span>
          </span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-soft">
            Ore nel mese
          </span>
          <span
            className={cx(
              "flex items-center gap-1.5 rounded-field bg-surface px-2.5",
              ore.trim() !== "" && !oreValide && "ring-1 ring-danger",
            )}
          >
            <input
              type="text"
              inputMode="decimal"
              value={ore}
              placeholder="12"
              aria-label="Ore nel mese"
              onChange={(e) => setOre(e.target.value)}
              className={campo}
            />
            <span className="text-[12px] text-ink-faint">ore</span>
          </span>
        </label>
      </div>

      <div className="flex items-baseline justify-between gap-3 rounded-field bg-surface px-3 py-2.5">
        <span className="text-[13px] text-ink-soft">Totale del mese</span>
        <span className="text-[18px] font-extrabold tabular-nums text-ink">
          {totaleCents === null ? "-" : formatEur(totaleCents)}
        </span>
      </div>

      <div className="flex justify-end gap-2">
        <Pill variant="ghost" size="sm" onClick={() => setAperto(false)}>
          Annulla
        </Pill>
        <Pill
          variant="primary"
          size="sm"
          icon={<Check size={14} />}
          onClick={() => void salva()}
          loading={salvando}
          disabled={totaleCents === null || ore.trim() === ""}
        >
          Salva
        </Pill>
      </div>
    </div>
  );
}

/**
 * Quanto è andato diversamente da prima. Le riserve stanno sotto il numero,
 * in chiaro: un giorno da questa differenza dipenderà una fattura.
 */
/**
 * Il confronto con i mesi di prima, in una riga.
 *
 * Prima elencava tutti i mesi del riferimento e tre avvertenze lunghe: sotto
 * un rendiconto si leggeva come un contratto (12/10/2026). Le riserve vere
 * restano, dette corte: il mese parziale o in corso lo dice già il
 * rendiconto, e una media di mesi diversi è un'indicazione, non una misura.
 * L'elenco dei mesi sta nel tooltip, per chi lo vuole.
 */
function Confronto({ confronto: c }: { confronto: ConfrontoBaseline }) {
  if (c.base === "insufficiente") return null;
  const differenza = c.differenzaCents ?? 0;
  const meglio = differenza > 0;
  const percento =
    c.differenzaPercento !== null
      ? ` (${meglio ? "+" : "−"}${Math.abs(Math.round(c.differenzaPercento))}%)`
      : "";
  return (
    <p
      className="border-t border-line pt-2 text-[13px]"
      title={c.mesiDelRiferimento.map((m) => formatMese(m)).join(", ")}
    >
      <b className={cx("tabular-nums", meglio ? "text-ok" : "text-ink")}>
        {meglio ? "+" : "−"}
        {formatEur(Math.abs(differenza))}
        {percento}
      </b>{" "}
      <span className="text-ink-soft">
        {c.base === "anno_precedente"
          ? "rispetto allo stesso mese dell'anno scorso."
          : "rispetto alla media dei tuoi mesi. Solo un'indicazione: ogni mese ha la sua stagione."}
      </span>
    </p>
  );
}

/**
 * Da quanto tempo questi numeri vengono dal gestionale, se è tanto.
 *
 * `null` sotto le sei ore: la riconciliazione gira ogni mezz'ora mentre Otto
 * è aperto, e segnalare una cache fresca sarebbe un avviso che si impara a
 * ignorare. Sopra, invece, va detto — senza webhook la cache può essere di
 * giorni prima e nessuno se ne accorge guardando la cifra.
 */
export function daQuanto(iso: string | null): string | null {
  if (!iso) return null;
  const quando = Date.parse(iso);
  if (Number.isNaN(quando)) return null;
  const ore = Math.floor((Date.now() - quando) / 3_600_000);
  if (ore < 6) return null;
  if (ore < 48) return `${ore} ore fa`;
  return `${Math.floor(ore / 24)} giorni fa`;
}
